import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, FileText, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { orderApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { Order, OrderSearchParams } from "@/models";

const PAGE_SIZE = 10;

const getBadgeVariant = (status: string) => {
  switch (status) {
    case "approved":
    case "Approved":
      return "success";
    case "under_review":
    case "In Progress":
      return "warning";
    case "pending_payment":
    case "Pending Payment":
      return "secondary";
    case "denied":
    case "Denied":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "approved":
      return "Approved";
    case "under_review":
      return "Under Review";
    case "pending_payment":
      return "Pending Payment";
    case "denied":
      return "Denied";
    default:
      return status.replace(/_/g, " ");
  }
};

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const searchParams: OrderSearchParams = {
    search: searchTerm,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE
  };
  
  const { data: ordersResponse, isLoading } = useQuery({
    queryKey: ['orders', searchParams],
    queryFn: async () => orderApi.list(searchParams),
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading orders",
          description: error.message || "Failed to load orders",
        });
      }
    }
  });

  const orders = ordersResponse?.data || [];

  const handleRowClick = (orderId: number) => {
    navigate(`/buyer/orders/${orderId}`);
  };

  const Pagination = () => {
    if (!ordersResponse) return null;

    const totalPages = ordersResponse.totalPages;
    const canGoPrevious = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    return (
      <div className="flex items-center justify-between px-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, ordersResponse.total)} of {ordersResponse.total} results
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setCurrentPage(1)}
              disabled={!canGoPrevious}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronLeft className="h-4 w-4" />
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canGoPrevious}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canGoNext}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setCurrentPage(totalPages)}
              disabled={!canGoNext}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronRight className="h-4 w-4" />
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Orders</h1>
          <p className="text-gray-500">
            View and manage your document orders
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Orders</CardTitle>
            <CardDescription>
              Browse through all your document orders
            </CardDescription>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
              </div>
            ) : orders.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Association</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order: Order) => (
                      <TableRow 
                        key={order.id} 
                        className="cursor-pointer"
                        onClick={() => handleRowClick(order.id)}
                      >
                        <TableCell>{order.id}</TableCell>
                        <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{order.association_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {order.items?.map(item => item.label).join(", ") || "No documents"}
                        </TableCell>
                        <TableCell>${order.total_amount?.toFixed(2) || "0.00"}</TableCell>
                        <TableCell>
                          <Badge variant={getBadgeVariant(order.status)}>
                            {getStatusText(order.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <Link to={`/buyer/orders/${order.id}`}>
                              Details
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination />
              </>
            ) : (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No orders found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchTerm ? "Try a different search term" : "You haven't placed any orders yet"}
                </p>
                {!searchTerm && (
                  <Button asChild>
                    <Link to="/buyer/search">
                      Request Documents
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BuyerLayout>
  );
};

export default Orders;
